"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { serviceStats } from "@/lib/services-data"
import type { Service } from "@/lib/services-data"
import { motion } from "framer-motion"
import {
  ArrowRight,
  Award,
  Briefcase,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  DollarSign,
  FileCheck,
  Globe,
  HelpCircle,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Shield,
  Truck
} from "lucide-react"
import Link from "next/link"
import { useState } from "react"

// Icon mapping - map slugs to icon components
const iconMap: Record<string, typeof Package> = {
  "household-shifting": Package,
  "local-shifting": Truck,
  "office-shifting": Briefcase,
  "international-moving": Globe,
  "motorcycle-shifting": Truck,
  "car-shifting": Truck,
  "bulk-shifting": Package,
  "pet-relocation": Package,
}

// Service type without icon component
type SerializableService = Omit<Service, "icon"> & {
  iconName: string
}

export function ServiceDetail({ service }: { service: SerializableService }) {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)
  const Icon = iconMap[service.iconName] || Package

  // Optimized animation variants - faster and smoother
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.4 } 
    },
  } as const

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  }

  // Process steps
  const processSteps = [
    { icon: Phone, title: "Get Quote", description: "Contact us for a free consultation and quote" },
    { icon: Calendar, title: "Schedule", description: "Choose a convenient date and time" },
    { icon: Package, title: "Packing", description: "Professional packing with premium materials" },
    { icon: Truck, title: "Transport", description: "Safe and secure transportation" },
    { icon: FileCheck, title: "Delivery", description: "Careful unpacking and setup" },
  ]


  return (
    <main className="min-h-screen bg-gradient-to-b from-background via-slate-50 to-white">
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#003DA5] via-blue-700 to-blue-900 text-white py-16 sm:py-24 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-yellow-300 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Service Icon */}
            <motion.div 
              className={`w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-8 rounded-3xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-2xl`}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, type: "spring" }}
            >
              <Icon size={64} className="text-white" />
            </motion.div>

            {/* Title and Description */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {service.title}
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-8">
              {service.fullDescription}
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <Award className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-medium">ISO Certified</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium">24/7 Support</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/get-quote">
                <Button size="lg" className="bg-[#FF7A00] hover:bg-orange-600 text-white font-semibold shadow-xl hover:shadow-2xl transition-all px-8 py-6 text-lg">
                  Get Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link href="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003DA5] bg-transparent font-semibold px-8 py-6 text-lg transition-all">
                  <Phone className="mr-2" size={20} />
                  Call Now
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Statistics Section */}
      <section className="py-16 sm:py-20 bg-white relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-200 rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-orange-200 rounded-full blur-2xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Trusted by <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] to-[#FF7A00]">Thousands</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our track record speaks for itself - delivering excellence in every move
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {serviceStats.map((stat, index) => {
              const StatIcon = stat.icon
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="group text-center p-8 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200 hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#003DA5] to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <StatIcon className="text-white" size={32} />
                  </div>
                  <div className="text-4xl sm:text-5xl font-bold text-[#003DA5] mb-2 group-hover:text-[#FF7A00] transition-colors">
                    {stat.label}
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">{stat.label}</div>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Features - Left Column */}
            <motion.div
              className="lg:col-span-2 space-y-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Enhanced Features Card */}
              <Card className="p-8 sm:p-10 bg-white border-2 border-[#003DA5]/10 shadow-xl rounded-3xl overflow-hidden relative">
                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-transparent rounded-full blur-3xl -z-0"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
                      <Check size={28} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                        What's <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] to-[#FF7A00]">Included</span>
                      </h2>
                      <p className="text-muted-foreground mt-1">Everything you need for a seamless move</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="group flex gap-3 items-start p-4 rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 hover:border-[#FF7A00]/30 hover:shadow-md transition-all duration-300"
                      >
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#FF7A00] to-orange-600 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                          <Check size={16} className="text-white" />
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Enhanced Process Section */}
              <Card className="p-8 sm:p-10 bg-gradient-to-br from-blue-50 via-white to-orange-50 border-2 border-slate-200 shadow-xl rounded-3xl overflow-hidden">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#003DA5] to-blue-700 flex items-center justify-center shadow-lg">
                    <Clock className="text-white" size={28} />
                  </div>
                  <div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                      How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] to-[#FF7A00]">Works</span>
                    </h2>
                    <p className="text-muted-foreground mt-1">Simple 5-step process for your move</p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {processSteps.map((step, index) => {
                      const StepIcon = step.icon
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="relative text-center group"
                        >
                          {/* Step Number */}
                          <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-[#FF7A00] to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg z-10">
                            {index + 1}
                          </div>
                          
                          <div className="p-6 bg-white rounded-2xl border-2 border-slate-200 hover:border-[#003DA5]/30 hover:shadow-lg transition-all duration-300 h-full">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#003DA5] to-blue-700 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md">
                              <StepIcon size={28} className="text-white" />
                            </div>
                            <div className="text-sm sm:text-base font-bold text-[#003DA5] mb-2">{step.title}</div>
                            <div className="text-xs text-muted-foreground leading-relaxed">{step.description}</div>
                          </div>

                          {/* Connector Arrow */}
                          {index < processSteps.length - 1 && (
                            <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                              <ArrowRight className="text-[#FF7A00]" size={24} />
                            </div>
                          )}
                        </motion.div>
                      )
                    })}
                  </div>
                </div>
              </Card>

              {/* Enhanced Benefits Section */}
              <Card className="p-8 sm:p-10 bg-gradient-to-br from-white to-green-50 border-2 border-green-200 shadow-xl rounded-3xl overflow-hidden relative">
                {/* Decorative Element */}
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-green-100 to-transparent rounded-full blur-3xl -z-0"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                      <Award size={28} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                        Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">Us</span>
                      </h2>
                      <p className="text-muted-foreground mt-1">Benefits that set us apart</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="group flex gap-3 items-start p-4 rounded-xl bg-white border border-green-200 hover:border-green-400 hover:shadow-md transition-all duration-300"
                      >
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform shadow-sm">
                          <Check size={16} className="text-white" />
                        </div>
                        <span className="text-foreground font-medium text-sm sm:text-base leading-relaxed">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Enhanced Sidebar - Right Column */}
            <motion.div
              className="lg:col-span-1 space-y-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Enhanced Pricing Card */}
              <Card className="p-8 bg-gradient-to-br from-[#FF7A00] via-orange-500 to-orange-600 text-white border-0 shadow-2xl  relative">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <DollarSign size={28} className="text-white" />
                    </div>
                    <h3 className="font-bold text-2xl">Get Started</h3>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-white/80 text-sm mb-2">Starting from</p>
                    <p className="font-bold text-4xl mb-3">{service.pricing}</p>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Get a personalized quote based on your specific requirements. No hidden charges!
                    </p>
                  </div>
                  
                  <Link href="/get-quote" className="block mb-4">
                    <Button className="w-full bg-white text-[#FF7A00] hover:bg-slate-100 font-bold shadow-lg hover:shadow-xl transition-all py-6 text-lg">
                      Get Free Quote
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                  
                  <Link href="/contact" className="block">
                    <Button variant="outline" className="w-full border-2 border-white/30 text-white hover:bg-white/10 bg-transparent font-semibold py-6">
                      <Phone className="mr-2" size={18} />
                      Call Now
                    </Button>
                  </Link>
                  
                  <div className="mt-6 pt-6 border-t border-white/20 space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <Shield size={16} />
                      </div>
                      <span className="font-medium">Full Insurance Coverage</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <Clock size={16} />
                      </div>
                      <span className="font-medium">24/7 Customer Support</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <Award size={16} />
                      </div>
                      <span className="font-medium">ISO Certified Service</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Enhanced Coverage Card */}
              <Card className="p-8 bg-white border-2 border-[#003DA5]/20 shadow-xl rounded-3xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#003DA5] to-blue-700 flex items-center justify-center shadow-lg">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-foreground">Service Areas</h3>
                    <p className="text-xs text-muted-foreground">We cover major cities</p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-6 max-h-64 overflow-y-auto custom-scrollbar">
                  {service.coverage.map((city, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-white border border-slate-200 hover:border-[#003DA5]/30 hover:shadow-sm transition-all group"
                    >
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF7A00] to-orange-600 group-hover:scale-150 transition-transform"></div>
                      <span className="text-sm font-medium text-foreground">{city}</span>
                    </motion.div>
                  ))}
                </div>
                
                <Link href="/locations" className="block">
                  <Button variant="outline" className="w-full border-2 border-[#003DA5] text-[#003DA5] hover:bg-[#003DA5] hover:text-white font-semibold transition-all">
                    <Globe className="mr-2" size={18} />
                    View All Locations
                  </Button>
                </Link>
              </Card>

              {/* Enhanced Quick Contact Card */}
              <Card className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 shadow-2xl rounded-3xl overflow-hidden relative">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF7A00]/20 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
                
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-[#FF7A00] flex items-center justify-center mb-4 shadow-lg">
                    <Phone size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold text-2xl mb-3">Need Help?</h3>
                  <p className="text-sm text-slate-300 mb-6 leading-relaxed">
                    Our expert team is available 24/7 to answer your questions and provide instant support.
                  </p>
                  <div className="space-y-3">
                    <Link href="/contact" className="block">
                      <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold shadow-lg">
                        <Phone size={16} className="mr-2" />
                        Contact Support
                      </Button>
                    </Link>
                    <Link href="/get-quote" className="block">
                      <Button variant="outline" className="w-full border-2 border-white/30 text-white hover:bg-white/10 bg-transparent font-semibold">
                        <Calendar size={16} className="mr-2" />
                        Book Consultation
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Enhanced FAQ Section */}
          <motion.section
            className="mt-20 sm:mt-28 relative"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            aria-labelledby="faq-heading"
          >
            {/* Background Elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>
            </div>

            <div className="text-center mb-16">
              <motion.div
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200/50 text-purple-700 rounded-full text-sm font-semibold mb-8 shadow-sm"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                  <HelpCircle className="w-3.5 h-3.5 text-white" />
                </div>
                Frequently Asked Questions
              </motion.div>
              
              <h2 id="faq-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
                  Got
                </span>{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] via-blue-600 to-[#FF7A00]">
                  Questions?
                </span>
              </h2>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                Find answers to the most common questions about our {service.title.toLowerCase()} service. 
                Can't find what you're looking for? Contact our expert team.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-slate-200">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-foreground">24/7 Support</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-slate-200">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-foreground">Quick Response</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-slate-200">
                  <Phone className="w-4 h-4 text-[#FF7A00]" />
                  <span className="font-semibold text-foreground">Expert Guidance</span>
                </div>
              </div>
            </div>
            
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 gap-5">
                {service.faq.map((item, index) => (
                  <motion.article
                    key={index}
                    className="group relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06, duration: 0.4 }}
                    itemScope
                    itemType="https://schema.org/Question"
                  >
                    <div className="relative border-2 border-slate-200 rounded-3xl overflow-hidden bg-white shadow-lg hover:shadow-2xl hover:border-[#003DA5]/30 transition-all duration-500">
                      {/* Decorative gradient on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-[#003DA5]/5 via-transparent to-[#FF7A00]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                      
                      <button
                        onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                        className="relative w-full p-6 sm:p-8 flex items-start justify-between gap-6 text-left group-hover:bg-gradient-to-r group-hover:from-blue-50/50 group-hover:to-orange-50/50 transition-all duration-300"
                        aria-expanded={expandedFAQ === index}
                        aria-controls={`faq-answer-${index}`}
                      >
                        <div className="flex items-start gap-5 flex-1">
                          <motion.div 
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg transition-all duration-300 ${
                              expandedFAQ === index 
                                ? 'bg-gradient-to-br from-[#FF7A00] to-orange-600 scale-110' 
                                : 'bg-gradient-to-br from-[#003DA5] to-blue-600 group-hover:scale-105'
                            }`}
                            whileHover={{ rotate: 5 }}
                          >
                            <span className="font-bold text-white text-base">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                          </motion.div>
                          
                          <div className="flex-1 pt-1">
                            <h3 
                              className="font-bold text-foreground text-lg sm:text-xl leading-relaxed group-hover:text-[#003DA5] transition-colors pr-4"
                              itemProp="name"
                            >
                              {item.question}
                            </h3>
                            {expandedFAQ !== index && (
                              <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                                Click to view answer
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <motion.div
                          animate={{ rotate: expandedFAQ === index ? 180 : 0 }}
                          transition={{ duration: 0.4, type: "spring" }}
                          className="flex-shrink-0"
                        >
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-md ${
                            expandedFAQ === index 
                              ? 'bg-gradient-to-br from-[#FF7A00] to-orange-600' 
                              : 'bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-slate-200 group-hover:to-slate-300'
                          }`}>
                            <ChevronDown size={24} className={`transition-colors ${expandedFAQ === index ? 'text-white' : 'text-slate-600'}`} />
                          </div>
                        </motion.div>
                      </button>
                      
                      <motion.div
                        id={`faq-answer-${index}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                          height: expandedFAQ === index ? "auto" : 0,
                          opacity: expandedFAQ === index ? 1 : 0,
                        }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="overflow-hidden"
                        role="region"
                        aria-labelledby={`faq-question-${index}`}
                      >
                        <div className="px-6 sm:px-8 pb-8">
                          <div 
                            className="ml-[68px] mr-4 relative"
                            itemScope
                            itemType="https://schema.org/Answer"
                            itemProp="acceptedAnswer"
                          >
                            {/* Decorative line */}
                            <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FF7A00] to-orange-600 rounded-full"></div>
                            
                            <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-orange-50/30 p-6 sm:p-8 rounded-2xl border-2 border-slate-100 shadow-inner">
                              <div className="flex items-start gap-3 mb-4">
                                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                                  <CheckCircle className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-sm font-bold text-green-700 uppercase tracking-wide pt-1">Answer</span>
                              </div>
                              <p 
                                className="text-muted-foreground text-base sm:text-lg leading-relaxed"
                                itemProp="text"
                              >
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </motion.section>
        </div>
      </section>
    </main>
  )
}
